// @flow
import { match } from '../helpers/adt';
import { option } from 'fp-ts';
import { type Option } from 'fp-ts/lib/Option';
import { doSnake, filterNotEmpty, isEmail, swatchAsset } from '../helpers/strings';
import { isValid, parse } from 'date-fns';
import { optionFind, prop } from '../helpers/functions';
import { last } from '../helpers/arrays';
import { SPECIAL_ATTRIBUTE_TYPES } from '../constants/attributes';
import type { PriceList, TierPrice } from '../flowtypes/product';
import { setQuantityError } from '../store/modules/quantity';
import { head } from 'fp-ts/lib/Array';

export type $GenericAttributeType = {
  id: string,
  type: '',
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
};

export type $TextAttributeType = {
  id: string,
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
  type: 'text',
};

export type $EmailAttributeType = {
  id: string,
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
  type: 'email',
};

export type $SelectAttributeType = {
  id: string,
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
  type: 'select',
  options: Array<any>,
};

export type $TextareaAttributeType = {
  id: string,
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
  type: 'textarea',
  maxLength: ?number,
};

export type $DateAttributeType = {
  id: string,
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
  type: 'date',
};

export type $AttributeOptionType = {
  id: string,
  hover_label: string,
  hover_description: string,
  hover_image: string,
  label: string,
  sort_order: number,
  value: string,
  display_label: string,
  type?: string,
  value_id?: number,
  hover_price?: string,
  products?: Array<any>,
  visible_on_pdp?: string,
};

export type $SwatchTextAttributeType = {
  id: string,
  type: '',
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
  type: 'swatch_text',
  options: Array<$AttributeOptionType>,
};

export type $SwatchImageAttributeType = {
  id: string,
  type: '',
  label: string,
  required: boolean,
  value?: string,
  position?: number,
  code?: string,
  type: 'swatch_visual_image',
  options: Array<$AttributeOptionType>,
};

export type $CustomOptionAttributeType = {
  id: string,
  label: string,
  required: boolean,
  value?: string,
  sort_order?: number,
  code?: string,
  type: 'drop_down',
  options: Array<$AttributeOptionType>,
  react_pdp_input_type: string,
  display_name: string
};

export type $AttributeUnionType =
  $GenericAttributeType
  | $TextAttributeType
  | $EmailAttributeType
  | $SelectAttributeType
  | $TextareaAttributeType
  | $SwatchTextAttributeType
  | $SwatchImageAttributeType
  | $DateAttributeType
  | $CustomOptionAttributeType;


type $AttributeEntityMatcher<T> = {
  Empty: (_: $GenericAttributeType) => T,
  Text: (_: $TextAttributeType) => T,
  Email: (_: $EmailAttributeType) => T,
  Select: (_: $SelectAttributeType) => T,
  Textarea: (_: $TextareaAttributeType) => T,
  Date: (_: $DateAttributeType) => T,
  SwatchText: (_: $SwatchTextAttributeType) => T,
  SwatchImage: (_: $SwatchImageAttributeType) => T,
  CustomOption: (_: $CustomOptionAttributeType) => T,
}

// Turn off eslint for next line because it doesn't understand the usage of generics here.
// eslint-disable-next-line
export type $AttributeEntity = <T>(_: $AttributeEntityMatcher<T>) => T;

// HELPERS
const CUSTOM_OPTION_ID_PREFIX = 'customOption--';
const intify = (str: string): number => parseInt(str, 10);
const boolify = (str: string): boolean => !!intify(str);

const formatCustomOptionValues = (values: Array<Object>): Array<$AttributeOptionType> => (
  values.map(value => ({
    ...value,
    value: prop('option_type_id')(value).getOrElseValue(''),
    id: prop('option_type_id')(value).getOrElseValue(''),
    hover_label: prop('hover_label')(value).getOrElseValue(''),
    hover_description: prop('hover_description')(value).getOrElseValue(''),
    hover_image: prop('hover_image')(value).getOrElseValue(''),
    label: prop('title')(value).getOrElseValue(''),
    display_label: prop('display_name')(value).filter(filterNotEmpty).getOrElseValue(prop('title')(value).getOrElseValue('')),
    sort_order: prop('sort_order')(value).map(intify).getOrElseValue(-1),
  }))
);

const convertCustomOptionToSwatchImage = (customOption: any) => ({
  ...customOption,
  label: customOption.title,
  id: `customOption--${customOption.option_id}`,
  type: 'swatch_visual_image',
  required: customOption.is_require,
  hidden: false,
  code: doSnake(customOption.title),
  options: customOption.values.map((optionValue) => ({
    ...optionValue,
    display_label: swatchAsset(optionValue.hover_image),
    hover_image: optionValue.hover_image,
    id: optionValue.option_type_id,
    value: optionValue.option_type_id,
    label: optionValue.default_title,
    type: 'swatch_visual_image',
  })),
});

const convertCustomOptionToSelect = (customOption: any) => ({
  ...customOption,
  label: customOption.title,
  id: `customOption--${customOption.option_id}`,
  type: 'select',
  required: customOption.is_require,
  hidden: false,
  code: doSnake(customOption.title),
  options: customOption.values.map((optionValue) => ({
    ...optionValue,
    display_label: optionValue.hover_label,
    id: optionValue.option_type_id,
    value: optionValue.option_type_id,
    label: optionValue.default_title,
    type: 'swatch_text',
  })),
});

const getOptionValue = <T>(opt: { id: T } | T): Option<T> => prop('id')(opt).alt(option.fromNullable(opt));

export const wrapCustomOptionId = (id: string): string => (
  !id.includes(CUSTOM_OPTION_ID_PREFIX) ? CUSTOM_OPTION_ID_PREFIX.concat(id) : id
);

export const unwrapCustomOptionId = (id: string): string => (
  last(id.split(CUSTOM_OPTION_ID_PREFIX))
);

/* Given an attribute, returns the special, overriding type value for that attribute based
   off of its 'code', or returns the attribute's existing type. */
export const formatAttributeType = (attr: $AttributeUnionType) => (
  prop(attr.code || '')(SPECIAL_ATTRIBUTE_TYPES).getOrElseValue(attr.type)
);

// CONSTRUCTORS
// Text :: $TextAttributeType => $AttributeEntity
export function TextAttribute(data: $TextAttributeType): $AttributeEntity {
  // The little "/* </> */" comment is just to make sure the syntax highlighter doesn't get confused.
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.Text(data);
}

// Email :: $EmailAttributeType => $AttributeEntity
export function EmailAttribute(data: $EmailAttributeType): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.Email(data);
}

// Select :: $SelectAttributeType => $AttributeEntity
export function SelectAttribute(data: $SelectAttributeType): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.Select(data);
}

// TextareaAttribute :: $TextareaAttributeType => $AttributeEntity
export function TextareaAttribute(data: $TextareaAttributeType): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.Textarea(data);
}

// DateAttribute :: $DateAttributeType => $AttributeEntity
export function DateAttribute(data: $DateAttributeType): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.Date(data);
}

// EmptyAttribute :: $GenericAttributeType => $AttributeEntity
export function EmptyAttribute(data: $GenericAttributeType): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.Empty(data);
}

// SwatchTextAttribute :: $GenericAttributeType => $AttributeEntity
export function SwatchTextAttribute(data: $SwatchTextAttributeType): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.SwatchText(data);
}

// SwatchImageAttribute :: $GenericAttributeType => $AttributeEntity
export function SwatchImageAttribute(data: $SwatchImageAttributeType): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => matcher.SwatchImage(data);
}

export function CustomOptionAttribute(props: *): $AttributeEntity {
  return <T>/* </> */(matcher: $AttributeEntityMatcher<T>): T => (
    matcher.CustomOption({
      ...props,
      id: prop('option_id')(props).alt(option.some('')).map(wrapCustomOptionId).getOrElseValue(''),
      required: prop('is_require')(props).map(boolify).getOrElseValue(false),
      sortOrder: prop('sort_order')(props).map(intify).getOrElseValue(0),
      options: prop('values')(props).map(formatCustomOptionValues).getOrElseValue([]),
      label: prop('title')(props).getOrElseValue(''),
      type: prop('type')(props).getOrElseValue(''),
    })
  );
}

/** */

export function Attribute(data: $AttributeUnionType): $AttributeEntity {
  switch (data.type) {
    case 'text':
      return TextAttribute(data);
    case 'email':
      return EmailAttribute(data);
    case 'select':
      return SelectAttribute(data);
    case 'textarea':
      return TextareaAttribute(data);
    case 'date':
      return DateAttribute(data);
    case 'swatch_text':
      return SwatchTextAttribute(data);
    case 'swatch_visual_image':
      return SwatchImageAttribute(data);
    case 'drop_down':
      switch (data.react_pdp_input_type) {
        case 'text_swatch':
          return CustomOptionAttribute(data);
        case 'image_swatch':
          return SwatchImageAttribute(convertCustomOptionToSwatchImage(data));
        case 'drop_down':
          return SelectAttribute(convertCustomOptionToSelect(data));
        default:
          return CustomOptionAttribute(data);
      }
    case 'radio':
      return SwatchImageAttribute(data);
    default:
      return EmptyAttribute(data);
  }
}

export const ErrorRequiredEmpty = 'requiredAndEmpty';
export const ErrorBrokenField = 'invalidField';
export const ErrorInvalidDate = 'invalidDate';
export const ErrorInvalidEmail = 'invalidEmail';
export const ErrorInvalidOption = 'invalidOption';
export const ErrorTextLength = 'invalidTextLength';

export const ValidationErrors = {
  [ErrorRequiredEmpty]: 'This field is required.',
  [ErrorInvalidEmail]: 'Please enter a valid email address.',
  [ErrorInvalidOption]: 'Please select an option.',
  // Theoretically, this message is never shown. Just type safety for covering Empty attribute type
  [ErrorBrokenField]: 'This field has an unsupported value',
  [ErrorInvalidDate]: 'The selected date is invalid.',
  [ErrorTextLength]: 'Maximum length exceeded.',
};

type $ValidationError = $Keys<typeof ValidationErrors>;
type $ValidationErrorMessage = $Values<typeof ValidationErrors>;

type $AttributeValidator = (fn: Function) => (error: $ValidationError) => (attr: $AttributeUnionType) => ?$ValidationErrorMessage;
const attributeValidator: $AttributeValidator = fn => err => val => (fn(option.fromNullable(val).getOrElseValue('')) ? err : null);


type $ValidationFunction = (attr: $AttributeUnionType) => ?$ValidationErrorMessage;
const validateIsEmpty: $ValidationFunction = attributeValidator(
  attr => (
    option
      .fromNullable(attr.value)
      .map(val => (typeof val === 'string' ? val.trim() === '' : false))
      .getOrElseValue(true)
  ),
)(ValidationErrors[ErrorRequiredEmpty]);

const validateIsNotEmail: $ValidationFunction = attributeValidator(attr => isEmail(attr.value))(ValidationErrors[ErrorInvalidEmail]);

const validateIsValidOption: $ValidationFunction = attributeValidator(
  attr => attr.options.find(opt => getOptionValue(opt) === attr.value),
)(ValidationErrors[ErrorInvalidOption]);

export const filterDependentOptions = (attr, index, full) => {
  // If this attribute/option doesn't have a dependent option, everything is good √
  if (!attr.depends_on_option) {
    return true;
  }
  // Otherwise we have to make sure the entire options set is correct
  return full
    // Filter out attributes/options that aren't depended on by this attribute/option
    .filter(_a => _a.title === attr.depends_on_option)
    // Filter out empty attributes
    .filter(_a => _a.value !== '')
    // Filter out attribute/option values that aren't applicable
    .filter(_a => _a.values
      // Filter out the attribute/option values that are not currently selected
      .filter(_v => _a.value === _v.option_type_id)
      // Filter out values that are not in the allowed values for this dependent option
      .filter(_v => attr.depends_on_values.split(',').includes(_v.title))
      .length > 0,
    ).length > 0;
};

export const optionHasDependentValues = (o): boolean => !!(o.depends_on_option && o.depends_on_values);

export const attributeHasDependentOptionValues = (attr): boolean =>
  attr.options && attr.options.reduce((a, o) => !!(a && optionHasDependentValues(o)));

export const valueDependsOnOptionWithoutValue = (o, full): boolean =>
  head(full.filter(a => a.title === o.depends_on_option)).map(a => a.value === '')
    .getOrElseValue(false);

export const mapDependentOptionvalues = (attr, index, full) => {
  // If there are no options with values that depend on other options, we are all good
  if (!attributeHasDependentOptionValues(attr)) {
    return attr;
  }
  // Need to take out impossible values
  return {
    ...attr,
    options: attr.options.map(v => {
      // If the option has a dependent option, but it's dependent option has no value, no changes should be made
      if (!optionHasDependentValues(v) || valueDependsOnOptionWithoutValue(v, full)) {
        return v;
      }

      // Get the attribute from the full attribute set that this value depends on
      const a = head(full.filter(_a => _a.title === v.depends_on_option)).getOrElseValue(false);
      if (a) {
        const values = v.depends_on_values.split(',');
        const selected = head(a.options.filter(o => o.value === a.value)).getOrElseValue(false);
        if (selected && values.includes(selected.title)) {
          return v;
        }
      }

      return null;
    }).filter(v => !!v),
  };
};

const validateIsValidDate: $ValidationFunction = attributeValidator(attr => !isValid(parse(attr.value)))(ValidationErrors[ErrorInvalidDate]);

const validateTextLength: $ValidationFunction = attributeValidator(attr => (attr.maxLength ? attr.value.length > attr.maxLength : false))(ValidationErrors[ErrorTextLength]);

type $ifRequiredOrHasValue = (attr: $AttributeUnionType) => (...fns: Array<Function>) => ?string
const ifRequiredOrHasValue: $ifRequiredOrHasValue = attr => (...fns) => (
    // eslint-disable-next-line
    attr.required && attr.required !== '0' || (attr.value && attr.value !== '') ? (
      fns.reduce((accum, fn) => (accum === null ? fn(attr) : accum), null)
    ) : null
  );

export const validateQuantity = (priceList: PriceList, quantity: number) => (dispatch: Function) => {
  // If we don't have advanced pricing, just return true because quantity is validated through attribute
  if (!priceList || priceList.length === 0 || !priceList[0]) {
    return true;
  }

  // Just validate that the requested quantity exists in at least one of the list's products
  if (priceList[0].tierPrices.find((tierPrice: TierPrice) => tierPrice.quantity === quantity)) {
    dispatch(setQuantityError(null));
    return true;
  }

  dispatch(setQuantityError(ValidationErrors[ErrorRequiredEmpty]));
  return false;
};

export const validateAttribute: (_: $AttributeEntity) => ?$ValidationErrorMessage = match({
  Text: attr => ifRequiredOrHasValue(attr)(validateIsEmpty, validateTextLength),
  Date: attr => ifRequiredOrHasValue(attr)(validateIsEmpty, validateIsValidDate),
  Textarea: attr => ifRequiredOrHasValue(attr)(validateTextLength),
  Email: attr => ifRequiredOrHasValue(attr)(validateIsEmpty, validateIsNotEmail),
  Select: attr => ifRequiredOrHasValue(attr)(validateIsEmpty, validateIsValidOption),
  SwatchText: attr => ifRequiredOrHasValue(attr)(validateIsEmpty, validateIsValidOption),
  SwatchImage: attr => ifRequiredOrHasValue(attr)(validateIsEmpty, validateIsValidOption),
  CustomOption: attr => ifRequiredOrHasValue(attr)(validateIsEmpty, validateIsValidOption),
  Empty: () => ErrorBrokenField,
});

export const getAttributeId: (_: $AttributeEntity) => string = match({
  Text: ({ id }) => id,
  Date: ({ id }) => id,
  Textarea: ({ id }) => id,
  Email: ({ id }) => id,
  Select: ({ id }) => id,
  SwatchText: ({ id }) => id,
  SwatchImage: ({ id }) => id,
  CustomOption: ({ id }) => id,
  Empty: ({ id }) => id,
});

export const getAttributeCode: (_: $AttributeEntity) => Option<string> = match({
  Text: ({ code }) => option.fromNullable(code),
  Date: ({ code }) => option.fromNullable(code),
  Textarea: ({ code }) => option.fromNullable(code),
  Email: ({ code }) => option.fromNullable(code),
  Select: ({ code }) => option.fromNullable(code),
  SwatchText: ({ code }) => option.fromNullable(code),
  SwatchImage: ({ code }) => option.fromNullable(code),
  CustomOption: (co: $CustomOptionAttributeType) => prop('title')(co).alt(prop('default_title')(co)),
  Empty: ({ code }) => option.fromNullable(code),
});

export const getAttributeSelectedSwatchOption: (_:$AttributeEntity) => Option<$AttributeOptionType> = match({
  Text: () => option.none,
  Date: () => option.none,
  Textarea: () => option.none,
  Email: () => option.none,
  Select: () => option.none,
  SwatchText: ({ value, options }) => optionFind(o => o.id === value, options),
  SwatchImage: ({ value, options }) => optionFind(o => o.id === value, options),
  CustomOption: ({ value, options }) => optionFind(o => o.id === value, options),
  Empty: () => option.none,
});

export const getSelectOptions: (_: $AttributeEntity) => Option<Array<{ label: string, value: any }>> = match({
  Text: () => option.none,
  Date: () => option.none,
  Textarea: () => option.none,
  Email: () => option.none,
  Select: ({ options }) => option.fromNullable(
    options.map(opt => ({
      label: prop('label')(opt).alt(prop('value')(opt)).alt(prop('website_value')(opt)).getOrElseValue(''),
      value: prop('id')(opt).alt(prop('website_value')(opt)).getOrElseValue(''),
    })),
  ),
  SwatchText: () => option.none,
  SwatchImage: () => option.none,
  CustomOption: () => option.none,
  Empty: () => option.none,
});

export const getAttributeSelectedSwatchOptionLabel = (attr :$AttributeEntity): Option<string> => (
  getAttributeSelectedSwatchOption(attr)
    .chain((opt: $AttributeOptionType) => {
      switch (opt.type) {
        case 'swatch_visual_image': {
          return option.fromNullable(opt.hover_label);
        }

        default: {
          return option.fromNullable(opt.display_label);
        }
      }
    })
);

export const extractAttribute: (_: $AttributeEntity) => $AttributeUnionType = match({
  Text: attr => attr,
  Date: attr => attr,
  Textarea: attr => attr,
  Email: attr => attr,
  Select: attr => attr,
  SwatchText: attr => attr,
  SwatchImage: attr => attr,
  CustomOption: attr => attr,
  Empty: attr => attr,
});

// PREDICATES
export const attributeIsCustomOption: (_: $AttributeEntity) => boolean = match({
  Text: () => false,
  Date: () => false,
  Textarea: () => false,
  Email: () => false,
  Select: () => false,
  SwatchText: () => false,
  SwatchImage: () => false,
  CustomOption: () => true,
  Empty: () => false,
});

// FILTER
export const filterConfigurableAttributes = (arr: Array<$AttributeUnionType>): Array<$AttributeUnionType> => (
  arr.filter(a => !attributeIsCustomOption(Attribute(a)))
);



// WEBPACK FOOTER //
// ./src/types/attributes.js